// utils/compCycle.js — comp-cycle UI helpers (item #1 part 2, UI-only).
//
// ★ MIRRORS the payroll engine's anchor derivation (computePayroll.js deriveBonusAnchor /
// deriveCommAnchor) for DISPLAY purposes only. Kept in utils/ (not engines/) deliberately so
// part 2 touches NO engine file — the engine remains the single source that actually derives
// the anchor at compute time; this only shows the operator what that derivation will produce.
// If the engine rule ever changes, update both (they are two lines each).
// NOTE: SUPERSEDED IN PART (kept as written above): since B1 this file IS the one owner -- computePayroll imports
// deriveBonusAnchor / deriveCommAnchor / FREQ_MONTHS from here, so there is nothing to keep in step any more.

export const FREQ_MONTHS = { monthly: 1, quarterly: 3, semi_annual: 6, annual: 12 };

// The frequency choices as shown to the operator (value = the stored token, label = the display word). Moved here
// from AdminPage's page-local BONUS_FREQ_OPTIONS (2026-09-29) so the employee template spec can import it.
export const FREQ_OPTIONS = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'semi_annual', label: 'Semi-Annual' },
    { value: 'annual', label: 'Annual' },
];

// BONUS anchor = the calendar period-END for the frequency, in the close-month year.
// bonusDelay = companies.bonus_payout_delay_months (0/1/2): period-END + N months (year rollover).
// ★ MIRRORS computePayroll.deriveBonusAnchor (display only — the Edit-Employee inherited value).
export function deriveBonusAnchor(basis, closeMonth, freq, bonusDelay = 0) {
  if (basis !== 'calendar_period_end' || !closeMonth || !FREQ_MONTHS[freq]) return null;
  const yr0 = Number(String(closeMonth).split('-')[0]);
  if (!yr0) return null;
  let mo = FREQ_MONTHS[freq], yr = yr0;
  const off = Number.isFinite(bonusDelay) ? bonusDelay : (parseInt(bonusDelay, 10) || 0);
  if (freq !== 'monthly' && off > 0) { mo += off; while (mo > 12) { mo -= 12; yr += 1; } }
  return `${yr}-${String(mo).padStart(2, '0')}`;
}

/* COMMISSION anchor = the period-START. SINCE 2026-10-09 (commission ship 1, Brian Q7): JANUARY OF THE WINDOW'S FIRST YEAR,
   so a rep with no commission_start_month cycles on CALENDAR periods (quarters Jan / Apr / Jul / Oct, halves Jan / Jul, years
   Jan) -- what "calendar_period_end" always meant. It used to return the first forecast month, which matched the calendar only
   because every tenant closes at a quarter end. A mid-period close now gives a cycle that STRADDLES the close; its closed
   months come from the engine's pre-close commission (engines/quotaEarnings.js, R12). The window always starts in a January
   (Step 1). Monthly cycles never read the anchor. THE ONE OWNER: the engine (computePayroll) and the Payroll page both call it. */
export function deriveCommAnchor(basis, allMonths, closeMonth) {
  if (basis !== 'calendar_period_end' || !closeMonth || !allMonths || !allMonths.length) return null;
  return String(allMonths[0]).slice(0, 4) + '-01';
}

/* N5 (Brian, 10-09): the commission cycle the Edit dialog shows as INHERITED -- the calendar period start that CONTAINS THE
   FIRST FORECAST MONTH, for this rep's frequency (quarterly from Apr 2023 shows Apr 2023; annual shows Jan 2023; monthly shows
   the first forecast month). Derived from deriveCommAnchor, so it names a month of the SAME cycles the engine uses. */
export function commCycleStartShown(basis, allMonths, closeMonth, freq) {
  const anchor = deriveCommAnchor(basis, allMonths, closeMonth);
  const first = anchor ? allMonths.find(m => m > closeMonth) : null;
  if (!anchor || !first) return null;
  const step = FREQ_MONTHS[freq] || 1;
  const [ay, am] = anchor.split('-').map(Number), [fy, fm] = first.split('-').map(Number);
  const k = Math.floor(((fy * 12 + fm - 1) - (ay * 12 + am - 1)) / step) * step;
  const abs = ay * 12 + am - 1 + k;
  return Math.floor(abs / 12) + '-' + String((abs % 12) + 1).padStart(2, '0');
}

// Selector options for the cycle basis. Only one policy exists today; the shape leaves room for
// future values (e.g. period-start, fiscal-offset) without touching callers.
export const COMP_CYCLE_BASIS_OPTIONS = [
  { value: 'calendar_period_end', label: 'Payouts at period-end of each period (calendar)' },
];

export function compCycleBasisLabel(v) {
  const o = COMP_CYCLE_BASIS_OPTIONS.find(x => x.value === v);
  return o ? o.label : (v || '—');
}
